// src/components/Auth/MockAuth0Provider.js
// Mock Auth0Provider for E2E testing - bypasses Auth0 authentication
import React, { createContext, useContext } from 'react';

const mockUser = {
  sub: 'auth0|mock-user-id',
  email: 'e2e-test@example.com',
  email_verified: true,
  name: 'E2E Test User',
  nickname: 'e2e-test',
  picture: 'https://via.placeholder.com/150',
  updated_at: new Date().toISOString(),
};

const mockAuthContext = {
  isAuthenticated: true,
  isLoading: false,
  user: mockUser,
  loginWithRedirect: () => Promise.resolve(),
  loginWithPopup: () => Promise.resolve(),
  logout: () => {
    // Clear mock auth state
    localStorage.removeItem('e2e_mock_auth_mode');
    window.location.href = '/';
  },
  getAccessTokenSilently: () => Promise.resolve('mock-access-token-for-e2e-tests'),
  getAccessTokenWithPopup: () => Promise.resolve('mock-access-token-for-e2e-tests'),
  getIdTokenClaims: () => Promise.resolve({
    __raw: 'mock-id-token',
    ...mockUser,
    iss: 'https://localhost/',
    aud: 'local-client-id',
    iat: Math.floor(Date.now() / 1000),
    exp: Math.floor(Date.now() / 1000) + 86400,
  }),
  error: undefined,
};

/**
 * Deliberate unauthenticated shape for the session-security suite.
 *
 * `ProtectedRoute` decides whether to render children by reading from
 * `useMockAuth0()` whenever `isE2ETestMode()` is true (see
 * `ProtectedRoute.js`). The previous build hardcoded that hook to
 * `mockAuthContext`, so the unauthenticated route tests in
 * `session-security.spec.ts` had no way to flip the decision back to
 * "block render" inside an E2E run. The same override flag honored by
 * `useAuthPatched` (`localStorage['e2e-auth-state'] === 'unauthenticated'`)
 * now also wins inside this hook so the auth-guard tests actually
 * verify the route protection rather than passing for unrelated reasons.
 *
 * The returned shape is strictly less privileged than the authenticated
 * mock — no user, no token, no claims — so even if a real user lands
 * on the flag the worst that happens is they look signed out.
 */
const unauthenticatedMockContext = {
  isAuthenticated: false,
  isLoading: false,
  user: undefined,
  loginWithRedirect: () => Promise.resolve(),
  loginWithPopup: () => Promise.resolve(),
  logout: () => {},
  getAccessTokenSilently: () => Promise.reject(new Error('Unauthenticated (E2E override)')),
  getAccessTokenWithPopup: () => Promise.reject(new Error('Unauthenticated (E2E override)')),
  getIdTokenClaims: () => Promise.resolve(undefined),
  error: undefined,
};

const isE2EUnauthenticatedOverride = () => {
  return (
    typeof window !== 'undefined' &&
    localStorage.getItem('e2e-auth-state') === 'unauthenticated'
  );
};

// Create a mock context that matches Auth0's context
const MockAuth0Context = createContext(mockAuthContext);

// Hook that matches useAuth0
export const useMockAuth0 = () => {
  const ctxValue = useContext(MockAuth0Context);
  // The override is checked AFTER `useContext` so the React Hook
  // rules stay intact; the conditional only switches the returned
  // value, never the hook call site.
  if (isE2EUnauthenticatedOverride()) {
    return unauthenticatedMockContext;
  }
  return ctxValue;
};

// Provider that matches Auth0Provider interface
export const MockAuth0Provider = ({ children }) => {
  return (
    <MockAuth0Context.Provider value={mockAuthContext}>
      {children}
    </MockAuth0Context.Provider>
  );
};

// Check if E2E test mode is enabled
//
// The unauthenticated override (`e2e-auth-state === 'unauthenticated'`)
// is treated as a positive signal here too. App.js gates the
// `MockAuth0Provider` wrap on this function, so without listing the
// override the session-security suite would set the flag, App would
// stay on the real Auth0 path, and the override (consumed via
// `useMockAuth0`) would never run. Listing the override keeps it
// self-sufficient — a test sets just the one flag and the rest of the
// auth tree resolves to an honest unauthenticated shape.
export const isE2ETestMode = () => {
  return (
    process.env.REACT_APP_E2E_TEST_MODE === 'true' ||
    (typeof window !== 'undefined' &&
      (localStorage.getItem('e2e_mock_auth_mode') === 'true' ||
        localStorage.getItem('e2e-auth-state') === 'unauthenticated'))
  );
};

export default MockAuth0Provider;
